// Which cubes a comparison can actually reach, and why the rest cannot.
//
// Analysis federates the shared containers — the copies published outbound plus
// whatever partners share inbound. A cube sitting in the private aggregates
// container is invisible to any query, however well it reconciles. So this is
// not a filter: an unreachable cube is returned, marked ineligible, and carries
// the sentence that says why. Hiding it would put the analyst back where the
// Catalog reader found them, looking at an absence with no way to tell which
// kind it is.
//
// Pure and dependency-free: it joins two API responses the client already holds
// (/api/cubes and, for a partner's declared measure, /api/catalog) rather than
// adding a third endpoint that would compose them server-side.

// Eligibility keys on "has a published copy", not on the state name.
// published-beyond-intent is a governance problem AND a perfectly readable cube;
// excluding it by name would have blocked it for the wrong reason.
const REASONS = {
  'private-by-intent': 'Private by intent — shared with nobody, so no query can reach it.',
  'publish-pending': 'Computed but not published yet — governance republishes before a query can reach it.',
  'not-computed': 'Declared but never computed — there is no file to query.',
  orphan: 'A cube file with no declaration behind it, and not in any shared container.',
};
const UNREACHABLE = 'Not in any shared container — Analysis reads only what was published, so a query cannot reach it.';

const periodsOf = (cube) => (cube.periods ?? []).map((p) => p.period).filter(Boolean);

// cubes   — the /api/cubes response: { publisher, measure, period, cubes[], shared[] }
// catalog — the /api/catalog response, optional. Only used for the anchor
//           partner's declared measure, which /api/cubes does not carry: the
//           cube reader classifies partner cells against OUR measure by design,
//           so it never learns theirs.
export function comparableCubes({ cubes = {}, catalog = null } = {}) {
  const ownMeasure = cubes.measure ?? null;
  const ownPublisher = cubes.publisher ?? 'This institution';

  const own = (cubes.cubes ?? []).map((c) => {
    const axes = c.dimensions ?? [];
    const published = (c.publishedTo ?? []).length > 0;
    return entry({
      publisher: ownPublisher,
      cubeId: c.id,
      axes,
      periods: periodsOf(c),
      measure: ownMeasure,
      own: true,
      // Order matters: a cube with a published copy but no axis is reachable and
      // still uncomparable, and saying "private" about it would be wrong.
      reason: !published ? (REASONS[c.state] ?? UNREACHABLE) : null,
    });
  });

  const partnerDatasets = catalog?.partner?.container
    ? { container: catalog.partner.container, datasets: catalog.partner.datasets ?? [] }
    : null;

  const shared = (cubes.shared ?? []).flatMap((s) => {
    const isAnchor = partnerDatasets && (s.containers ?? []).includes(partnerDatasets.container);
    return (s.cubes ?? []).map((c) => {
      const declared = isAnchor
        ? partnerDatasets.datasets.find((d) => d.dataSet === c.id)
        : null;
      return entry({
        publisher: s.label,
        cubeId: c.id,
        axes: c.dimensions ?? [],
        periods: periodsOf(c),
        // Their manifest's measure when we have it. Falling back to ours is a
        // guess, and a wrong guess shows up as an empty block in the preview
        // rather than as a wrong number.
        measure: declared?.measure ?? ownMeasure,
        measureAssumed: !declared,
        own: false,
        // In a container we read by definition — that is how we came to have
        // its cells at all.
        reason: null,
      });
    });
  });

  return [...own, ...shared];
}

function entry({ publisher, cubeId, axes, periods, measure, own, reason, measureAssumed = false }) {
  // A cube whose only column is the period has nothing to break down by. Rare,
  // but it would otherwise offer an empty axis select.
  const noAxis = axes.length ? null : 'No dimension to compare — the cube carries counts and a period, nothing to break them down by.';
  const blocked = reason ?? noAxis;
  return {
    key: `${publisher}::${cubeId}`,
    publisher,
    cubeId,
    axes,
    periods,
    measure,
    measureAssumed,
    own,
    eligible: !blocked,
    reason: blocked,
  };
}

// The picker groups by publisher; own institution first, then partners in the
// order the reader returned them.
export function byPublisher(entries) {
  const order = [];
  const groups = new Map();
  for (const e of entries) {
    if (!groups.has(e.publisher)) { groups.set(e.publisher, []); order.push(e.publisher); }
    groups.get(e.publisher).push(e);
  }
  return order.map((publisher) => ({ publisher, cubes: groups.get(publisher) }));
}
